import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Button, message } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../../Shared/Header';
import backIcon from "../../../assets/icons/backIcon.svg";
import { createLeafAnalysis, getLastLeafAnalysis } from '../../../Utils/analysis/leafAnalysis';
import CustomDatePicker from '../../Shared/CustomDatePicker';

const CreateLeafAnalysis = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { exploitationId } = useParams();
    const [loading, setLoading] = useState(false);
    const [lastAnalysis, setLastAnalysis] = useState(null);

    useEffect(() => {
        const fetchLastAnalysis = async () => {
            try {
                const lastAnalysis = await getLastLeafAnalysis(exploitationId);
                if (lastAnalysis) {
                    setLastAnalysis(lastAnalysis);
                    const prefillData = {};
                    Object.keys(lastAnalysis).forEach(key => {
                        if (key.endsWith('_min') || key.endsWith('_max')) {
                            prefillData[key] = lastAnalysis[key];
                        }
                    });
                    form.setFieldsValue(prefillData);
                }
            } catch (error) {
                console.error('Error fetching last leaf analysis:', error);
                message.error('Failed to fetch last leaf analysis data');
            }
        };

        fetchLastAnalysis();
    }, [exploitationId, form]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const formattedValues = {
                ...values,
                date_realisation: values.date_realisation.toISOString(),
                exploitation_id: parseInt(exploitationId),
            };
            await createLeafAnalysis(formattedValues);
            message.success('Analyse foliaire créée avec succès');
            navigate(`/admin/exploitation/${exploitationId}/analysis/leaf`);
        } catch (error) {
            console.error('Erreur lors de la création de l\'analyse foliaire:', error);
            message.error('Échec de la création de l\'analyse foliaire');
        } finally {
            setLoading(false);
        }
    };

    const duplicateLastAnalysis = () => {
        if (!lastAnalysis) {
            message.error('Aucune analyse précédente trouvée');
            return;
        }
        const prefillData = { ...lastAnalysis };
        // remove the id and date_realisation and assign the rest of the values to the form
        delete prefillData.id;
        delete prefillData.date_realisation;
        form.setFieldsValue(prefillData);
        message.success('Analyse précédente dupliquée avec succès');
    };



    const leafAnalysisParameters = [
        { key: 'n', label: 'Azote (N) % de MS' },
        { key: 'p', label: 'Phosphore (P) % de MS' },
        { key: 'k', label: 'Potassium (K) % de MS' },
        { key: 'mg', label: 'Magnésium (Mg) % de MS' },
        { key: 'ca', label: 'Calcium (Ca) % de MS' },
        { key: 'cl', label: 'Chlorures (Cl) % de MS' },
        { key: 'na', label: 'Sodium (Na) mg/kg de MS' },
        { key: 'zn', label: 'Zinc (Zn) mg/kg de MS' },
        { key: 'cu', label: 'Cuivre (Cu) mg/kg de MS' },
        { key: 'mn', label: 'Manganèse (Mn) mg/kg de MS' },
        { key: 'fe', label: 'Fer (Fe) mg/kg de MS' },
        { key: 'b', label: 'Bore (B) mg/kg de MS' },
    ];

    const HeaderTab = ({ text }) => (
        <th className="border border-gray-300 bg-gray-300 px-2 py-2">{text}</th>
    );

    return (
        <div className="bg-white h-full">
            <Header title="Créer une Analyse Foliaire" />
            <div className="h-[calc(100%-100px)] p-6">
                <Link
                    to={`/admin/exploitation/${exploitationId}/analysis/feuilles`}
                    className="cursor-pointer flex pl-5 gap-2 mt-4"
                >
                    <img src={backIcon} alt="" className="w-5" />
                    <div className="font-semibold text-base text-indigo-600">Retour</div>
                </Link>

                <div className='max-w-[1000px] mx-auto'>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-2xl font-semibold">Nouvelle Analyse Foliaire</h2>
                        <Button onClick={duplicateLastAnalysis} type='secondary' className='text-md border-indigo-600 border-2 text-indigo-600 hover:bg-indigo-600 hover:text-white'>
                            Dupliquer la dernière analyse
                        </Button>
                    </div>
                    <Form
                        form={form}
                        name="createLeafAnalysis"
                        onFinish={onFinish}
                        layout="vertical"
                        className='bg-white p-6 rounded-lg max-w-[1000px] shadow-md'
                    >
                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <Form.Item name="date_realisation" label="Date de réalisation" rules={[{ required: true }]}>
                                <CustomDatePicker />
                            </Form.Item>
                            <Form.Item name="laboratoire" label="Laboratoire">
                                <Input />
                            </Form.Item>
                            <Form.Item name="echantillon" label="Échantillon">
                                <Input />
                            </Form.Item>
                            <Form.Item name="leaf_name" label="Nom de la feuille">
                                <Input />
                            </Form.Item>
                            <Form.Item name="vanne_associee" label="Vanne associée">
                                <Input />
                            </Form.Item>
                        </div>
                        <h2 className="text-xl font-semibold mt-6 mb-4">Paramètres de l'analyse foliaire</h2>
                        <table className="w-full border-collapse border border-gray-300 mb-4">
                            <thead>
                                <tr>
                                    <HeaderTab text="Paramètre" />
                                    <HeaderTab text="Min" />
                                    <HeaderTab text="Max" />
                                    <HeaderTab text="Données" />
                                </tr>
                            </thead>
                            <tbody>
                                {leafAnalysisParameters.map(param => (
                                    <tr key={param.key}>
                                        <td className="border border-gray-300 px-2 py-2">{param.label}</td>
                                        <td className="border border-gray-300 px-2 py-2">
                                            <Form.Item name={`${param.key}_min`} noStyle>
                                                <InputNumber step={0.01} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </td>
                                        <td className="border border-gray-300 px-2 py-2">
                                            <Form.Item name={`${param.key}_max`} noStyle>
                                                <InputNumber step={0.01} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </td>
                                        <td className="border border-gray-300 px-2 py-2">
                                            <Form.Item name={`${param.key}_data`} noStyle>
                                                <InputNumber step={0.01} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <Form.Item className="mt-4">
                            <Button type="primary" htmlType="submit" loading={loading}>
                                Créer l'Analyse Foliaire
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default CreateLeafAnalysis;